import { FC, useEffect, useState } from "react";
import FrameOneModal from "./modalComponents/FrameOneModal";
import FrameModalHeader from "./modalComponents/FrameModalHeader";
import FrameModalBody from "./modalComponents/FrameModalBody";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import ThirdwebLogin from "@/pages/ThirdwebLogin";
import { useGetGuild } from "@/hooks/useGetGuild";
import useThemePreference from "@/hooks/useThemePreference";
import { useAsset } from "@/hooks/useAsset";
import classNames from "classnames";
import { useSearchParams } from "react-router";
import { isMobile } from "react-device-detect";
import { InvitedWelcomeModal } from "../InvitedWelcomeModal";
import { useGetChallenge } from "@/hooks/challenges/useGetChallenge";

interface props {
    isOpen: boolean;
    onClose: () => void;
    forceDarkMode?: boolean;
}

const LoginModal: FC<props> = ({ isOpen, onClose, forceDarkMode }) => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const isOnOverwolf = useSelector((store: IStore) => store.metaStore.isOnOverwolf);
    const referralCode = useSelector((store: IStore) => store.affiliateStore.referralCode);
    const [showInvitedModal, setShowInvitedModal] = useState(false);
    const { isOnDarkMode } = useThemePreference();
    const [searchParams] = useSearchParams();
    const guildInviteId = searchParams.get("guildId");
    const challengeInviteID = searchParams.get("challengeId");
    const {data: guildInfo} = useGetGuild(guildInviteId);
    const {data: challengeInfo} = useGetChallenge(challengeInviteID);
    const guildAssetImage = useAsset(guildInfo?.hero);
    const challengeAssetImage = useAsset(challengeInfo?.backgroundImageSmall);
    const isInvite = !!referralCode;
    const isGuildInvite = isInvite && guildInviteId;
    const isChallengeInvite = isInvite && challengeInviteID;
    useEffect(() => {
        if (isOpen && fullToken) {
            onClose();
        }
    }, [fullToken?.id, isOpen]);

    const getBannerImage = () => {
        if (isGuildInvite) {
            return guildAssetImage?.data?.cdnUrl;
        }
        if (isChallengeInvite) {
            return challengeAssetImage?.data?.cdnUrl;
        }
        return `${import.meta.env.VITE_CDN_URL}/images/referral-modal-${isMobile ? "sm" : "lg"}.webp`;
    };


    const getLogo = () => {
        if (isChallengeInvite) {
            return null;
        }
        // devourplay logo
        if (isOnOverwolf) {
            return <img
                src={`${import.meta.env.VITE_CDN_URL}/images/DevourPlayLogoWhite.webp`}
                alt="Devour logo"
                className="overwolf-landing-page_login-logo"
            />;
        }
        // generic invite logo
        if (isInvite) {
            return <img
                src={`${import.meta.env.VITE_CDN_URL}/images/devour-logo-white.png`}
                alt="Devour logo"
                style={{
                    height: "auto",
                    width: "6rem",
                }}
            />;
        }
        // default logo
        return (
            <img
                src="/images/devourplay-logo.png"
                alt="DevourPlay Logo"
                style={{ width: "200px", height: "auto" }}
            />
        );


    };

    const handleInvitedRegistered = () => {
        if (isInvite && !isChallengeInvite) {
            setShowInvitedModal(true);
        }
    };

    // Don't render modal if user is already logged in and modal should be closed
    if (fullToken && !isOpen && !showInvitedModal) {
        return null;
    }

    return (
        <>
            <InvitedWelcomeModal isVisible={showInvitedModal} onClose={() => {
                setShowInvitedModal(false);
            }}/>
            <FrameOneModal
                contentClassName={classNames("magic-login-modal", {
                    "is-invite": !!referralCode,
                })}
                containerClassName="magic-login-modal-container"
                size={isOnOverwolf ? "xs2" : "sm2"}
                isOpen={isOpen}
            // toggle={onClose} Accidental closing the login modal will require the user to get a new code and invalid their previous one
            >
                {isInvite && <>
                    <div
                        className="invite-modal_background"
                        style={{
                            backgroundImage: `url(${getBannerImage()})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            height: "8.73556rem",
                        }}
                    ></div>
                    <div
                        className="invite-modal_shader"
                        style={{
                            background: `linear-gradient(180deg, rgba(45, 45, 47, 0.10) 0%, ${
                                isOnDarkMode || forceDarkMode ? "#2D2D2F" : "white"
                            } 7rem)`,
                        }}
                    ></div>
                </>}
                <FrameModalHeader toggle={onClose}>
                    {getLogo()}
                </FrameModalHeader>
                <FrameModalBody>
                    <ThirdwebLogin isOnDialog={true} onInvitedRegistered={handleInvitedRegistered} />
                </FrameModalBody>
            </FrameOneModal>
        </>
    );
};

export default LoginModal;